import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { GoSync } from "react-icons/go";
import Button from "../../components/Button";
import DownloadLink from "../../components/DownloadLink";
import ImageShow from "../../components/ImageShow";
import Toast from "../../components/Toast";
import { env } from "../../constants/env-config";
import { useUpdatePrevPeriodTradingLossMutation } from "../../store/apis/taxApi";

export function YearSection({ data }) {
	const { t } = useTranslation();
	const { formData, yearData } = data || {};
	const { id, year, tradingProfit, dividendsIncome, otherIncome, totalTax, prevPeriodTradingLoss } = yearData?.data || {};
	const taxReportsArray = JSON.parse(env.TAX_REPORTS.replace(/'/g, '"'));
	
	const [updatePrevPeriodTradingLoss, { status: updateStatus }] = useUpdatePrevPeriodTradingLossMutation();
	const [isEditingLoss, setIsEditingLoss] = useState(false);
	const [lossValue, setLossValue] = useState(prevPeriodTradingLoss || 0);
	const [errorMessage, setErrorMessage] = useState("");
	const [validationError, setValidationError] = useState("");

	const fileNames = [
		...(taxReportsArray.includes("details") ? [{
			name: t('taxReports.documents.detailedCalculation'),
			document: "excel",
		}] : []),
		...(taxReportsArray.includes("f1") ? [{ name: t('taxReports.documents.f1Form'), document: "f1Form", path: '/f1-form' }] : []),
		...(taxReportsArray.includes("declaration") ? [{
			name: t('taxReports.documents.declaration'),
			document: "declaration"
		}] : []),
		...(taxReportsArray.includes("coverLetter") ? [
			{
				name: t('taxReports.documents.coverLetter'),
				document: "coverLetter",
				downloadName: 'cover-letter',
				downloadType: '.docx',
			},
		] : []),
	]

	const handleButtonClick = () => {
    const newTab = window.open('https://www.youtube.com/watch?v=' + env.DECLARATION_PAGE_VIDEO_URL_ID, '_blank', 'noopener,noreferrer');
    if (newTab) newTab.focus();
  };

	const handleSaveLoss = () => {
		setErrorMessage("");
		setValidationError("");
		
		const numValue = parseFloat(lossValue);
		
		if (isNaN(numValue)) {
			setValidationError(t('taxReports.prevPeriodTradingLoss.invalidInput'));
			return;
		}
		const lossToSend = numValue > 0 ? -numValue : numValue;
		
		updatePrevPeriodTradingLoss({
			id,
			prevPeriodTradingLoss: lossToSend,
		})
		.unwrap()
		.then(() => {
			setIsEditingLoss(false);
			setLossValue(numValue);
			setErrorMessage("");
			setValidationError("");
		})
		.catch((err) => {
			const errorMsg = err?.commonMessage || t('taxReports.prevPeriodTradingLoss.error');
			setErrorMessage(errorMsg);
		});
	};

	const handleCancel = () => {
		setIsEditingLoss(false);
		setLossValue(prevPeriodTradingLoss || 0);
		setErrorMessage("");
		setValidationError("");
	};

  const filesTax = useMemo(() => fileNames.map((file) => {
		const handleDownload = (event) => {
			event.preventDefault();
			const fileData = formData[file.document].data;

			const data = fileData;

			if (data) {
				const url = URL.createObjectURL(data);
	
				const link = document.createElement("a");
				link.href = url;
				link.download = `${file.downloadName || file.document}-${year}${file.downloadType || ''}`;
				document.body.appendChild(link);
	
				link.click();
	
				document.body.removeChild(link);
				URL.revokeObjectURL(url);
			}
		};

		return (
      <div
        key={file.name}
        className="flex bg-gray-25 py-5 pl-6 pr-4 border-b border-gray-75">
        <ImageShow
          className="mr-2 h-[24px] w-[18px]"
          image={{ url: "paper", label: "paper" }}
        />
        <p>{file.name}</p>
				<Button className="ml-auto" onClick={handleDownload}>
					<ImageShow
						className="mr-0"
						image={{ url: "download", label: "download" }}
					/>
				</Button>
      </div>
    );
  }), [fileNames, data]);

	return (
		<div className="flex flex-col">
			<div className="flex flex-col bg-gray-75 rounded-2xl mb-4 p-6">
				<div className="flex flex-col">
					<div className="flex items-center gap-2 mb-3">
						<h3 className="text-sm font-medium text-gray-350">{t('taxReports.prevPeriodTradingLoss.title')}</h3>
					</div>
					{!isEditingLoss ? (
						<div className="flex items-center gap-3">
							<span className="text-sm font-medium">{prevPeriodTradingLoss ?? 0} {t('currency')}.</span>
							<button
								onClick={() => setIsEditingLoss(true)}
								className="text-xs px-3 py-1 rounded bg-gradient-to-r from-cyan-650 to-cyan-75 text-white hover:from-cyan-900 hover:to-cyan-650 transition">
								{t('taxReports.prevPeriodTradingLoss.edit')}
							</button>
						</div>
					) : (
						<div className="flex flex-col gap-3">
							<input
								type="number"
								value={lossValue}
								onChange={(e) => {
									setLossValue(e.target.value);
									setErrorMessage("");
									setValidationError("");
								}}
								className="px-3 py-2 border border-gray-75 rounded text-sm focus:outline-none focus:border-cyan-650 focus:ring-1 focus:ring-cyan-650"
								placeholder="0"
								disabled={updateStatus === 'pending'}
							/>
							<div className="flex gap-2 items-center">
								<button
									onClick={handleSaveLoss}
									disabled={updateStatus === 'pending'}
									className="text-xs px-3 py-1 rounded bg-gradient-to-r from-cyan-650 to-cyan-75 text-white hover:from-cyan-900 hover:to-cyan-650 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1 transition">
									{updateStatus === 'pending' && <GoSync className="animate-spin w-3 h-3" />}
									{updateStatus === 'pending' ? t('taxReports.prevPeriodTradingLoss.saving') : t('taxReports.prevPeriodTradingLoss.save')}
								</button>
								<button
									onClick={handleCancel}
									disabled={updateStatus === 'pending'}
									className="text-xs px-3 py-1 rounded bg-gray-25 text-gray-350 hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed transition">
									{t('taxReports.prevPeriodTradingLoss.cancel')}
								</button>
							</div>
							{validationError && (
								<p className="text-xs text-red-600 mt-1">{validationError}</p>
							)}
						</div>
					)}
				</div>
			</div>

			{errorMessage && (
				<Toast
					message={errorMessage}
					type="error"
					duration={5000}
					onClose={() => setErrorMessage("")}
				/>
			)}

			<div
				className="flex flex-wrap items-center border-gray-75 pb-2 
				sm:pb-0 bg-gray-75 rounded-2xl [&+div]:border-t-white"
			>
				<div className="w-[85%] sm:w-auto order-1 flex py-5">
					<div className="ml-6 flex justify-center text-[28px] h-[36px] text-gray-350 ">
						<ImageShow image={{ url: "folder", label: "folder" }} />
					</div>
					<div className="flex items-center">
						<p>{t('taxReports.declarations')} {year}</p>
					</div>
				</div>
				<div className="order-3 sm:order-2 mr-8 text-sm ml-6 sm:ml-auto">
					<p className="text-xs text-gray-350">{t('taxReports.tradingOperations')}</p>
					<span className="text-sm">{tradingProfit} {t('currency')}.</span>
				</div>
				<div className="order-4 sm:order-3 mr-8">
					<p className="text-xs text-gray-350">{t('taxReports.dividends')}</p>
					<span className="text-sm">{dividendsIncome} {t('currency')}.</span>
				</div>
				<div className="order-5 sm:order-4 mr-8">
					<p className="text-xs text-gray-350">{t('taxReports.otherIncome')}</p>
					<span className="text-sm">{otherIncome} {t('currency')}.</span>
				</div>
				<div className="order-6 sm:order-5 mr-8">
					<p className="text-xs text-gray-350">{t('taxReports.taxToPay')}</p>
					<span className="text-sm">{totalTax} {t('currency')}.</span>
				</div>
				{/* <div className="order-2 sm:order-6 place-self-end self-center mr-5 ml-auto sm:ml-0 sm:mr-4">
					<DownloadLink data="" />
				</div> */}
				<div className="order-7 text-sm flex flex-col basis-full [&>*:last-child]:rounded-b-2xl">
					{filesTax}
				</div>
			</div>
				<Button 
					className="
						text-sm text-gray-350 mt-1 rounded-md py-2.5 px-4 w-fit
						bg-gradient-to-r from-cyan-650 to-cyan-75 text-white 
						hover:bg-gradient-to-r hover:from-cyan-900 hover:to-cyan-650
						flex gap-2 mt-4
					"
					onClick={handleButtonClick}
				>
					<svg width="22" height="22" viewBox="0 0 22 22" fill="none" xmlns="http://www.w3.org/2000/svg">
						<g clipPath="url(#clip0_2855_1981)">
						<path d="M17.4167 22H4.58333C3.3682 21.9985 2.20326 21.5152 1.34403 20.656C0.484808 19.7967 0.00145554 18.6318 0 17.4167L0 4.58333C0.00145554 3.3682 0.484808 2.20326 1.34403 1.34403C2.20326 0.484808 3.3682 0.00145554 4.58333 0L17.4167 0C18.6318 0.00145554 19.7967 0.484808 20.656 1.34403C21.5152 2.20326 21.9985 3.3682 22 4.58333V17.4167C21.9985 18.6318 21.5152 19.7967 20.656 20.656C19.7967 21.5152 18.6318 21.9985 17.4167 22ZM4.58333 1.83333C3.85399 1.83333 3.15451 2.12306 2.63879 2.63879C2.12306 3.15451 1.83333 3.85399 1.83333 4.58333V17.4167C1.83333 18.146 2.12306 18.8455 2.63879 19.3612C3.15451 19.8769 3.85399 20.1667 4.58333 20.1667H17.4167C18.146 20.1667 18.8455 19.8769 19.3612 19.3612C19.8769 18.8455 20.1667 18.146 20.1667 17.4167V4.58333C20.1667 3.85399 19.8769 3.15451 19.3612 2.63879C18.8455 2.12306 18.146 1.83333 17.4167 1.83333H4.58333ZM8.5635 15.5879C8.18137 15.5866 7.80634 15.4845 7.47633 15.2918C7.15032 15.1052 6.87972 14.8354 6.69225 14.5099C6.50477 14.1844 6.40713 13.8149 6.40933 13.4393V8.56075C6.40902 8.18505 6.50752 7.81587 6.69495 7.49026C6.88238 7.16465 7.15214 6.89406 7.47717 6.70564C7.80221 6.51721 8.17108 6.41758 8.54678 6.41673C8.92248 6.41589 9.29179 6.51387 9.61767 6.70083L14.4558 9.11625C14.7919 9.29767 15.0733 9.56567 15.271 9.89247C15.4686 10.2193 15.5752 10.593 15.5798 10.9749C15.5844 11.3568 15.4868 11.7329 15.297 12.0644C15.1073 12.3958 14.8324 12.6705 14.5008 12.8599L9.57275 15.323C9.2654 15.4983 8.91733 15.5897 8.5635 15.5879ZM8.54058 8.25458C8.49006 8.25456 8.44043 8.26784 8.39667 8.29308C8.34912 8.31952 8.30968 8.35841 8.28255 8.40556C8.25542 8.45271 8.24163 8.50636 8.24267 8.56075V13.4393C8.24298 13.4928 8.25722 13.5454 8.284 13.5918C8.31077 13.6382 8.34916 13.6769 8.39539 13.704C8.44162 13.7311 8.49411 13.7457 8.54769 13.7464C8.60127 13.747 8.6541 13.7338 8.701 13.7078L13.629 11.2438C13.6657 11.2151 13.6949 11.1779 13.714 11.1355C13.7332 11.093 13.7418 11.0465 13.739 11C13.7401 10.9455 13.7263 10.8917 13.699 10.8445C13.6717 10.7973 13.6319 10.7585 13.5841 10.7323L8.74958 8.31692C8.68655 8.27825 8.6145 8.25676 8.54058 8.25458Z" fill="white"/>
						</g>
						<defs>
						<clipPath id="clip0_2855_1981">
						<rect width="22" height="22" fill="white"/>
						</clipPath>
						</defs>
					</svg>
				{t('taxReports.documents.submissionInstruction')}
				</Button>
			<div className="h-10 mt-8 text-gray-350 text-sm pt-2 pl-3 rounded-lg bg-gray-25 w-full">
				{t('taxReports.modifyDeclarationContact')}
				{ ' ' }
				<a href="https://t.me/OSAtax" target="_blank" rel="noopener noreferrer" className="underline">
          {t('taxReports.contactUs')}
        </a>
				{/* <Link to="/reports" className="underline">
					рахунки
				</Link> */}
			</div>
		</div>
	);
}

	// const fileNames = [
	// 	{
	// 		name: t('taxReports.documents.declaration'),
	// 		document: "declaration"
	// 	},
	// 	{ name: t('taxReports.documents.f1Form'), document: "f1Form", path: '/f1-form' },
	// 	{
	// 		name: t('taxReports.documents.coverLetter'),
	// 		document: "coverLetter",
	// 		downloadName: 'cover-letter',
	// 		downloadType: '.docx',
	// 		// type: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
	// 		// type: 'application/octet-stream'
	// 	},
	// 	{
	// 		name: t('taxReports.documents.detailedCalculation'),
	// 		document: "excel",
	// 	}
	// ];
